import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, AlertCircle } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InformationCard,
  Pagination,
  TabNavigation,
} from '@/components/ui'
import type {
  ImportedImage,
  TrolleySheetData,
} from '@/utils/tool-management/tool-management.util'
import { usePreviewImportModal } from '@/hooks/useToolManagement/usePreviewImportModal'

import { ConfirmCancelModal } from './ConfirmCancelModal'
import { ConfirmReplaceModal } from './ConfirmReplaceModal'
import { PreviewTable } from './PreviewTable'

interface PreviewImportModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onBack: () => void
  onConfirm: () => Promise<void>
  onCancel?: () => void
  data: Nullable<TrolleySheetData>
  images?: ImportedImage[]
  isConfirming?: boolean
  onImagesUpdate?: (images: ImportedImage[]) => void
  hasExistingTrolleys?: boolean
}

export function PreviewImportModal({
  isOpen,
  onOpenChange,
  onBack,
  onConfirm,
  onCancel,
  data,
  images = [],
  isConfirming = false,
  onImagesUpdate,
  hasExistingTrolleys = false,
}: PreviewImportModalProps) {
  const { t } = useTranslation()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)

  const {
    fileInputRef,
    selectedTrolley,
    selectedDrawer,
    trolleyNames,
    trolleyCounts,
    drawers,
    drawerItemCounts,
    items,
    hasEmptyRows,
    imageMap,
    currentPage,
    totalPages,
    currentTrolleyDescription,
    setCurrentPage,
    setSelectedTrolley,
    setSelectedDrawer,
    handleImageClick,
    handleImageRemove,
    handleImageUpload,
  } = usePreviewImportModal(isOpen, data, images, onImagesUpdate)

  const handleConfirm = async () => {
    if (!onConfirm) return

    if (hasExistingTrolleys) {
      setShowConfirmModal(true)
    } else {
      await onConfirm()
    }
  }

  const handleConfirmReplace = async () => {
    setShowConfirmModal(false)
    if (onConfirm) {
      await onConfirm()
    }
  }

  const handleCancelReplace = () => {
    setShowConfirmModal(false)
  }

  const handleCancel = () => {
    setShowCancelModal(false)
    onOpenChange(false)
    if (onCancel) {
      onCancel()
    }
  }

  const confirmButtonLabel = isConfirming
    ? t('common.processing', 'Processing...')
    : t('common.confirm')

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className='max-w-6xl max-h-[90vh] overflow-y-auto overflow-x-hidden'
          showCloseButton={false}
        >
          <DialogHeader className='flex flex-row items-center justify-between space-y-0 pb-4 border-b border-[#D5D7DA] w-full'>
            <div className='flex items-center gap-2 flex-shrink-0'>
              <ChevronLeft
                className='h-5 w-5 cursor-pointer'
                onClick={() => {
                  onOpenChange(false)
                  onBack()
                }}
              />
              <div>
                <DialogTitle className='text-lg font-semibold'>
                  {t('toolManagement.toolListPreview')}
                </DialogTitle>
                <DialogDescription className='text-xs text-gray-500 mt-1'>
                  {t('toolManagement.reviewAndEditImage')}
                </DialogDescription>
              </div>
            </div>
            <div className='flex gap-3 flex-shrink-0'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setShowCancelModal(true)}
                disabled={isConfirming}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className='bg-red-600 hover:bg-red-700'
                onClick={handleConfirm}
                disabled={isConfirming}
              >
                {confirmButtonLabel}
              </Button>
            </div>
          </DialogHeader>

          <div className='flex flex-col space-y-6 overflow-x-hidden min-h-0'>
            <TabNavigation
              items={trolleyNames}
              selectedItem={selectedTrolley}
              counts={trolleyCounts}
              onItemSelect={trolleyName => {
                setSelectedTrolley(trolleyName)
                setSelectedDrawer(null)
              }}
              className='mt-4'
            />

            {currentTrolleyDescription && (
              <p className='text-sm'>{currentTrolleyDescription}</p>
            )}

            {drawers.length > 0 && (
              <div className='inline-block ml-1'>
                <select
                  value={selectedDrawer || ''}
                  onChange={e => {
                    setSelectedDrawer(e.target.value)
                    e.target.blur()
                  }}
                  className='px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500'
                >
                  {drawers.map(drawer => (
                    <option key={drawer} value={drawer}>
                      {drawer} ({drawerItemCounts[drawer] || 0})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className='flex-1 overflow-y-auto overflow-x-auto min-h-0'>
              <PreviewTable
                items={items}
                imageMap={imageMap}
                onImageClick={handleImageClick}
                onImageRemove={handleImageRemove}
              />
            </div>

            {totalPages > 1 && (
              <div className='flex justify-end'>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}

            {hasEmptyRows && (
              <InformationCard
                icon={<AlertCircle className='h-4 w-4 text-red-500' />}
                text={t('toolManagement.invalidRowsWarning')}
                className='text-red-500'
              />
            )}
          </div>

          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            className='hidden'
            onChange={handleImageUpload}
          />
        </DialogContent>
      </Dialog>

      <ConfirmReplaceModal
        isOpen={showConfirmModal}
        onClose={handleCancelReplace}
        onConfirm={handleConfirmReplace}
      />

      <ConfirmCancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
      />
    </>
  )
}
